import { translateApiMessages, translateFieldErrors } from './translateApiMessage';

export const AVATAR_MAX_BYTES = 4 * 1024 * 1024;

export const PAYLOAD_TOO_LARGE_MESSAGE =
  'The uploaded image is too large. Please use an image up to 4 MB.';

export function extractApiErrorPayload(error) {
  return error?.response?.data ?? error ?? {};
}

export function flattenValidationErrors(errors) {
  if (!errors || typeof errors !== 'object') {
    return [];
  }

  return Object.values(errors).flat().filter(Boolean);
}

export async function readApiJson(response) {
  const text = await response.text();
  if (!text) {
    return {};
  }
  try {
    return JSON.parse(text);
  } catch {
    return {};
  }
}

export function normalizeUploadError(response, data, fallbackMessage = 'Something went wrong') {
  if (response?.status === 413) {
    const message =
      typeof data?.message === 'string' && data.message.trim() !== ''
        ? data.message
        : PAYLOAD_TOO_LARGE_MESSAGE;
    return { ...data, message, status: 413 };
  }

  if (data && typeof data === 'object' && Object.keys(data).length > 0) {
    return data;
  }

  return { message: fallbackMessage };
}

export function getApiErrorMessages(payload, fallbackMessage = 'Something went wrong') {
  const data = extractApiErrorPayload(payload);
  const messages = flattenValidationErrors(data?.errors);

  if (messages.length > 0) {
    return messages;
  }

  if (payload?.status === 413 || data?.status === 413) {
    if (typeof data?.message === 'string' && data.message.trim() !== '') {
      return [data.message];
    }
    return [PAYLOAD_TOO_LARGE_MESSAGE];
  }

  if (typeof data?.message === 'string' && data.message.trim() !== '') {
    return [data.message];
  }

  if (typeof payload === 'string' && payload.trim() !== '') {
    return [payload];
  }

  return [fallbackMessage];
}

export function formatApiError(payload, fallbackMessage = 'Something went wrong', language) {
  return translateApiMessages(getApiErrorMessages(payload, fallbackMessage), language).join(' • ');
}

export function enrichApiError(error, fallbackMessage = 'Something went wrong') {
  const payload = extractApiErrorPayload(error);
  const messages = getApiErrorMessages(error, fallbackMessage);

  if (error && typeof error === 'object') {
    error.apiMessages = messages;
    error.formattedMessage = translateApiMessages(messages).join(' • ');
    error.fieldErrors = payload?.errors ?? null;
  }

  return error;
}

export function applyValidationErrorsToForm(form, errors, language) {
  if (!form || !errors || typeof errors !== 'object') {
    return;
  }

  const translatedErrors = translateFieldErrors(errors, language);

  form.setFields(
    Object.entries(translatedErrors).map(([name, fieldErrors]) => ({
      name,
      errors: Array.isArray(fieldErrors) ? fieldErrors : [String(fieldErrors)],
    })),
  );
}
